import {
  Button,
  Container,
  Header,
  Select,
  SpaceBetween,
  Popover,
  DateRangePicker,
  ExpandableSection,
  FormField,
  Link, 
  Flashbar,
  Input,
  Box,
  Grid,
  Multiselect,
  TokenGroup,
  Alert,
} from '@amzn/awsui-components-react';
import { CommonDomainProps } from '../../models/types';
import {  ESEC2 } from '../../../models/types';
import { useParams } from 'react-router';
import { useEffect, useState, useRef } from 'react';
import { useRequestOnDemandRTFMetricsMutation } from '../OnDemand/useRequestOnDemandRTFMetrics'
import moment from 'moment';
import {
  i18nStrings, isValidRequestRangeForOnDemand,
  relativeOptions,
  REQUEST_RTF_METRICS_INSTRUCTION_HEADER,
  REQUEST_RTF_METRICS_INSTRUCTIONS, 
  timezoneOptions,
  metricsOptions
} from '../Logs/constants';
import { get } from 'lodash';
import { useGetOnDemandDomainQuery } from '../OnDemand/useGetOnDemandDomainQuery';
import { ListExecutions } from "../OnDemand/ListExecutions";
import { 
  isVersionSupported, 
  isWithinOneMonth, 
  isDateRangeValid,
  renderRTFMetricsDescription 
} from './RTFUtils';

// Interface definition
interface RTFMetricsProps extends CommonDomainProps {
  domainIdentifier: string;
  ec2Instances: ESEC2[]
  authUser: boolean;
  esVersion: string; 
}

const RTFMetrics = (props: RTFMetricsProps) => {
  // Check if the version supports RTF Metrics
  const isSupported = isVersionSupported(props.esVersion);

  // Basic arguments
  const params = useParams();
  const clientId = params['clientId'];
  const domainName = params['domainName'];
  const domainId = `${clientId}:${domainName}`;
  const [domainQuery, setDomainQuery] = useState(false);

  // ListExecutions reference
  const ListExecutionsRef = useRef();

  // Available instances from props
  const [availableInstances, setAvailableInstances] = useState<{label: string, value: string}[]>([]);
  const [selectedAvailableInstances, setSelectedAvailableInstances] = useState<{label: string, value: string}[]>([]);
  
  // Custom instance ID input state
  const [currentInstanceId, setCurrentInstanceId] = useState('');
  const [customInstanceIds, setCustomInstanceIds] = useState<string[]>([]);
  const [instanceIdsError, setInstanceIdsError] = useState('');

  // Metrics selection state
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [metricsError, setMetricsError] = useState('');

  // Process instance IDs from props
  useEffect(() => {
  if (props.ec2Instances && props.ec2Instances.length > 0) {

    const instanceIds = props.ec2Instances.map((instance) => instance.instanceId);
      const options = instanceIds.map(id => ({
      label: id,
      value: id
    }));
    
    setAvailableInstances(options);
  }
}, [props.ec2Instances]); // Correct dependency

  // Combine all instance IDs from both selection methods
  const getAllInstanceIds = () => {
    const fromDropdown = selectedAvailableInstances.map(option => option.value);
    return [...new Set([...fromDropdown, ...customInstanceIds])]; // Remove duplicates
  };

  // Generate TokenGroup items from all instance IDs
  const instanceIdTokens = getAllInstanceIds().map(id => ({
    label: id,
    dismissLabel: `Remove instance ID ${id}`
  }));
  
  // Render Kibana Link
  const [getOnDemandDomain, { loading: getDomainLoading, data: getDomainResponse, error: getDomainError }] = useGetOnDemandDomainQuery();
  const domainResponse = get(getDomainResponse, 'getOnDemandDomain', '');
  
  useEffect(() => {
    getOnDemandDomain({
      variables: {
        domainId: domainId
      }
    });
  }, [domainQuery]);

  // Handle instance ID input change
  const handleInstanceIdChange = (e) => {
    setCurrentInstanceId(e.detail.value);
    setInstanceIdsError('');
  };

  // Handle adding custom instance ID
  const handleAddInstanceId = () => {
    const trimmedId = currentInstanceId.trim();
    
    // Validate the instance ID
    if (!trimmedId) {
      setInstanceIdsError('Cannot add empty Instance ID');
      return;
    }

    // Check for duplicates in both lists
    if (customInstanceIds.includes(trimmedId) || 
        selectedAvailableInstances.some(option => option.value === trimmedId)) {
      setInstanceIdsError('This instance ID is already added');
      return;
    }

    // Add to the custom list
    setCustomInstanceIds([...customInstanceIds, trimmedId]);
    setCurrentInstanceId(''); 
    setInstanceIdsError('');
  };

  // Handle selecting available instances
  const handleAvailableInstancesChange = ({ detail }) => {
    setSelectedAvailableInstances(detail.selectedOptions);
  };

  // Handle dismissing instance ID from TokenGroup
  const handleDismissInstanceId = ({ detail: { itemIndex } }) => {
    const allIds = getAllInstanceIds();
    const idToRemove = allIds[itemIndex];
    
    // Remove from the appropriate list
    if (customInstanceIds.includes(idToRemove)) {
      setCustomInstanceIds(customInstanceIds.filter(id => id !== idToRemove));
    } else {
      setSelectedAvailableInstances(selectedAvailableInstances.filter(
        option => option.value !== idToRemove
      ));
    }
  };

  // Handle pressing Enter key in the input field
  const handleKeyPress = (e) => {
    if (e.detail.key === 'Enter') {
      e.preventDefault();
      handleAddInstanceId();
    }
  };

  // Handle metrics selection change
  const handleMetricsChange = ({ detail }) => {
    setSelectedMetrics(detail.selectedOptions);
    setMetricsError('');
  };

  // Handle request metrics
  const [requestOnDemandRTFMetrics] = useRequestOnDemandRTFMetricsMutation();
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [submit, setSubmit] = useState(false);
  const [dateRangeError, setDateRangeError] = useState('');

  
  const handleRequestMetrics = async () => {
    // First validate the date range
    if (!isDateRangeValid(requestDate)) {
      setDateRangeError('RTF Metrics data is only available for the past 1 month');
      return;
    }

    const metricsList = selectedMetrics.map(metric => metric.value);
    const allInstanceIds = getAllInstanceIds();

    var startMoment, endMoment;

    if ('absolute' === requestDate.type) {
      startMoment = moment(requestDate.startDate);
      endMoment = moment(requestDate.endDate);
    } else if ('relative' === requestDate.type) {
      startMoment = moment().subtract(requestDate.amount, requestDate.unit);
      endMoment = moment();
    }

    const startTimeStamp = startMoment.utc().format('YYYYMMDDTHHmm') + 'Z';
    const endTimeStamp = endMoment.utc().format('YYYYMMDDTHHmm') + 'Z';
    setStartTime(startTimeStamp);
    setEndTime(endTimeStamp);
    
    try {
      const variables: any = {
        domainId: clientId + ':' + domainName,
        eventType: 'RTF',
        startTime: startTimeStamp,
        endTime: endTimeStamp,
        reBackfill: 'false',
        instanceIds: allInstanceIds,
        metrics: metricsList
      };
        
      var resp = await requestOnDemandRTFMetrics({ variables });      
      if (resp?.data?.requestOnDemandRTFMetrics?.success) {
        const status = resp?.data?.requestOnDemandRTFMetrics?.data;
        setFlashbarItems([
          {
            type: 'success',
            content: status,
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setFlashbarItems([]),
          },
        ]);
        
        // Refresh the general execution list using callRefreshExecutionList instead of refresh
        if (ListExecutionsRef.current && ListExecutionsRef.current.callRefreshExecutionList) {
          ListExecutionsRef.current.callRefreshExecutionList();
        }
      } else {
        const errorMessage = resp?.data?.requestOnDemandRTFMetrics?.error;
        setFlashbarItems([
          {
            type: 'error',
            content: `Error occurred while requesting RTF metrics: ${errorMessage}`,
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setFlashbarItems([]),
            id: 'rtf_metrics_error',
          },
        ]);
      }
      
      setSubmit(true);
    } catch (e) {
      setFlashbarItems([
        {
          type: 'error',
          content: `Unexpected error requesting RTF metrics: ${e.message}`,
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems([]),
          id: 'rtf_metrics_exception',
        },
      ]);
    }
  };

  const handleRequestDateChange = ({ detail }) => {
    setRequestDate(detail.value);
    // Validate the date range and update error message
    if (!isDateRangeValid(detail.value)) {
      setDateRangeError('RTF Metrics data is only available for the past 1 month');
    } else {
      setDateRangeError('');
    }
  };

  const handleRequestTimezoneChange = (selectedOption) => {
    setSelectedRequestTimeZoneOption(selectedOption);

  };

  // Handler for custom date validation
  const validateDateRange = (range) => {
    const isValid = isValidRequestRangeForOnDemand(range); // Original validation

    // Add our additional validation for 1 month
    if (isValid && range.type === 'absolute') {
      return isWithinOneMonth(range.startDate);
    }
    
    return isValid;
  };

  // Your timezone and date states...
  const [selectedRequestTimeZoneOption, setSelectedRequestTimeZoneOption] = useState({
    inputDisplay: 'Browser Timezone',
    label: 'Browser Timezone',
    value: 'Browser Timezone',
    code: new Date()
      .toLocaleTimeString('en-us', { timeZoneName: 'short' })
      .split(' ')[2],
    offset: '',
    mins: new Date().getTimezoneOffset() * -1,
  });

  const [requestDate, setRequestDate] = useState<any>({
    type: 'relative',
    amount: 3,
    unit: 'hour',
  });

  // Flashbar items
  const [flashbarItems, setFlashbarItems] = useState([]);

  // If version is not supported, show message and return
  if (!isSupported) {
    return (
      <Container>
        <Alert
          type="warning"
          header="RTF Metrics Not Available"
        >
          <p>
            RTF Metrics are only available for OpenSearch domains with version OS_2.15 or higher.
            Your current domain version is {props.esVersion || 'unknown'}.
          </p>
        </Alert>
      </Container>
    );
  }

  // React component default return
  return (
    <>
      <SpaceBetween direction="vertical" size="l">
        <Container
          header={
            <div>
              <Header variant="h2">RTF Metrics</Header>
            </div>
          }
        >
          <div>{renderRTFMetricsDescription()}</div>
        </Container>
        <Container>
          <Flashbar items={flashbarItems} />
          <ExpandableSection
            header={
              <Header
                variant="h2"
                info={
                  <div
                    style={{ display: 'inline-block' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Popover
                      dismissAriaLabel="Close"
                      fixedWidth
                      header={REQUEST_RTF_METRICS_INSTRUCTION_HEADER}
                      position="right"
                      size="large"
                      triggerType="text"
                      renderWithPortal={true}
                      content={REQUEST_RTF_METRICS_INSTRUCTIONS.map((line) => (
                      <p>{line}</p>
                      ))}
                    >
                      <Link variant="info">Info</Link>
                    </Popover>
                  </div>
                }
              >
                Request RTF Metrics Data
              </Header>
            }
            variant="container"
          >
            <SpaceBetween direction="vertical" size="l">
              {/* Alert for 1-month limitation */}
              <Alert type="info" header="Time Range Limitation">
                RTF Metrics data is only available for the past 1 month
              </Alert>
              {/* Date and Time Zone selection */}
              <SpaceBetween direction="horizontal" size="l">
                <FormField label={<strong>Date</strong>}>
                  <DateRangePicker 
                    style={{ marginRight: '20px', marginLeft : '20px' }}
                    onChange={handleRequestDateChange}
                    value={requestDate}
                    relativeOptions={relativeOptions}
                    i18nStrings={i18nStrings}
                    placeholder="Filter by a date and time range"
                    isValidRange={isValidRequestRangeForOnDemand}
                    showClearButton={false}
                    timeOffset={selectedRequestTimeZoneOption.mins}
                  />
                </FormField>
                <FormField label={<strong>Time Zone</strong>}>
                  <Select 
                    style={{ marginRight: '20px', marginLeft : '20px' }}
                    selectedOption={selectedRequestTimeZoneOption}
                    onChange={({ detail }) => handleRequestTimezoneChange(detail.selectedOption)}
                    options={timezoneOptions}
                    selectedAriaLabel="Selected"
                  />
                </FormField>
              </SpaceBetween>

              {/* Available Instance IDs dropdown */}
              {availableInstances.length > 0 && (
                <FormField 
                  label={<strong>Available Instances</strong>}
                  description="Select from current instances in the domain"
                >
                  <Multiselect
                    selectedOptions={selectedAvailableInstances}
                    onChange={handleAvailableInstancesChange}
                    options={availableInstances}
                    placeholder="Select from available instances"
                    selectedAriaLabel="Selected"
                    filteringType="auto"
                  />
                </FormField>
              )}

              {/* Non Active Instance IDs input */}
              <FormField 
                label={<strong>Add Previous Instance IDs</strong>}
                description="Enter instance IDs that were part of this cluster but are not listed in Available Instances
"
                errorText={instanceIdsError}
              >
                <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
                  <Input
                    value={currentInstanceId}
                    onChange={handleInstanceIdChange}
                    onKeyDown={handleKeyPress}
                    placeholder="Enter an instance ID (e.g., i-1234abcd)"
                  />
                  <Button
                    iconName="add-plus"
                    variant="normal"
                    onClick={handleAddInstanceId}
                    ariaLabel="Add instance ID"
                  />
                </Grid>
              </FormField>

              {/* Display all selected instance IDs as tokens */}
              <FormField 
                label={<strong>Selected Instance IDs</strong>}
                description="These instance IDs will be used for metrics collection"
              >
                <div style={{ minHeight: '10px' }}>
                  {instanceIdTokens.length > 0 ? (
                    <TokenGroup 
                      onDismiss={handleDismissInstanceId}
                      items={instanceIdTokens}
                    />
                  ) : (
                    <div style={{ color: '#666', fontStyle: 'italic', marginTop: '8px' }}>
                      No instance IDs selected (will collect data for all instances)
                    </div>
                  )}
                </div>
              </FormField>

              {/* Metrics selection */}
              <FormField 
                label={<strong>Metrics</strong>}
                description="Select specific metrics or leave empty to collect all available metrics"
                errorText={metricsError}
              >
                <Multiselect
                  selectedOptions={selectedMetrics}
                  onChange={handleMetricsChange}
                  options={metricsOptions}
                  placeholder="Select specific metrics (optional)"
                  selectedAriaLabel="Selected"
                  filteringType="auto"
                />
              </FormField>

              {/* Submit button */}
              <Box textAlign="right">
                <Button 
                  variant="primary" 
                  onClick={handleRequestMetrics}
                  disabled={!!dateRangeError} // Disable button if there's a date range error

                >
                  Submit Request
                </Button>
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        </Container>
        
        {/* Always show general ListExecutions */}
        <Container>
          <ListExecutions type='RTF' ref={ListExecutionsRef}/>
        </Container>
        
        <Container>
          <div>
            <Header variant="h3">
              Kibana Dashboard Link:{' '}
              <a
                href={`https://${domainResponse}/_dashboards`}
                target="_blank"
                rel="noopener noreferrer"
              >
                RTF Metrics Dashboard
              </a>
            </Header>
            <p>
              The above dashboard contains metrics and visualizations for Real-Time Field data. Login to the dashboard and follow the below
              steps:
            </p>
            <ul>
              <li>1: Authenticate yourself by signing with your corporate ID i.e by clicking on 'Amazon'.</li>
              <li>2: Click on the sidebar. Click on Dashboard and open 'RTF Metrics' dashboards according to your use-case.</li>
              <li>3: Add the domain ident in the filter for which you want to see the metrics.</li>
              <li>4: Choose the timestamp between which you requested the RTF Metrics.</li>
              <li>5: Filter by instance IDs and specific metrics if specified in your request.</li>
            </ul>
          </div>
        </Container>
      </SpaceBetween>
    </>
  );
}

export { RTFMetrics };